import { Box, FormControlLabel, Grid2, Radio, RadioGroup, Stack, Switch, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { ReferralLevel } from 'src/global/enums/project.enum';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';
import { isAllowedPercentage } from 'src/utils/numberFormat';

import { AdminNumberFormat } from '../AdminNumberFormat';
import { AdminInput } from '../AdminTextField';

export function TokenReward({
  isDisabled,
  defaultIsDonationEnable,
  defaultIsEnable,
}: {
  isDisabled?: boolean;
  defaultIsDonationEnable?: boolean;
  defaultIsEnable?: boolean;
}) {
  const { hasDeployed } = useDeployment();
  const {
    control,
    formState: { errors },
    setValue,
    watch,
    trigger,
    clearErrors,
  } = useFormContext<ProjectInputs>();
  const watchReferral = watch('referral');
  const watchReferralPercentage = useWatch({ control, name: 'referral.percentage' });
  const isFirstLevelReferral = watchReferral.referralLv === ReferralLevel.LEVEL_1;
  // Auto-update Level 1 Percentage when main percentage changes and First-Level Referrals is selected
  useEffect(() => {
    if (watchReferral?.referralLv === ReferralLevel.LEVEL_1 && watchReferralPercentage !== undefined) {
      setValue('referral.referLv1', watchReferralPercentage, { shouldValidate: false });
    }
  }, [watchReferralPercentage, watchReferral?.referralLv, setValue]);

  const triggerPercentageValidation = () => {
    trigger('referral.percentage');
    trigger('referral.referLv1');
    trigger('referral.referLv2');

    if (watchReferral?.isEnableDonation) {
      trigger('referral.donation');
    }
  };

  const handleReferralLevelChange = (e: any) => {
    const newLevel = e.target.value;
    setValue('referral.referralLv', newLevel);

    if (newLevel === ReferralLevel.LEVEL_1) {
      setValue('referral.referLv2', 0, { shouldValidate: false });
      setValue('referral.referLv1', watchReferralPercentage, { shouldValidate: false });
      clearErrors('referral.referLv2');
    } else if (newLevel === ReferralLevel.LEVEL_2) {
      clearErrors('referral.referLv2');
    }

    setTimeout(() => {
      trigger('referral.referLv1');
      if (newLevel === ReferralLevel.LEVEL_2) {
        trigger('referral.referLv2');
      }
    }, 0);
  };

  return (
    <Stack gap={3} width={'100%'} marginTop={3}>
      <Box width={'100%'}>
        <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
          Percentage
        </Typography>
        <Controller
          name='referral.percentage'
          control={control}
          render={({ field }) => (
            <AdminNumberFormat
              isAllowed={isAllowedPercentage}
              readOnly={(hasDeployed && defaultIsEnable) || isDisabled}
              onValueChange={values => {
                const percentage = values.floatValue ? Number(values.floatValue) : 0;
                field.onChange(percentage);

                const newPhases = watch('phases').map(phase => ({
                  ...phase,
                  referalPercent: percentage,
                  rewardType: watchReferral.rewardType,
                }));
                setValue('phases', newPhases);

                // Trigger validation for related fields
                triggerPercentageValidation();
              }}
              isError={!!errors?.referral?.percentage}
              errorMsg={errors?.referral?.percentage?.message}
              placeholder={'0%'}
              {...field}
            />
          )}
        />
      </Box>
      <Box width={'100%'}>
        <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
          Unallocated Funds Receiving Address
        </Typography>
        <Controller
          name='referral.allocatedAddress'
          control={control}
          render={({ field }) => (
            <AdminInput
              isError={!!errors?.referral?.allocatedAddress}
              errorMsg={errors.referral?.allocatedAddress?.message}
              placeholder={'Enter wallet address'}
              type='text'
              readOnly={(hasDeployed && defaultIsEnable) || isDisabled}
              {...field}
            />
          )}
        />
      </Box>
      {/* <Box>
        <Stack direction={'row'} justifyContent={'space-between'} width={'100%'}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <img src='/icons/common/one-tree-planted.svg' height={30} width={'auto'} alt='one-tree' />
            <Typography color='white' fontSize={'16px'} fontWeight={500} variant='h6'>
              Donation
            </Typography>
          </Stack>
          <Controller
            name='referral.isEnableDonation'
            control={control}
            render={({ field: { onChange, value } }) => (
              <Switch
                disabled={(hasDeployed && defaultIsDonationEnable) || isDisabled}
                checked={value}
                onChange={e => {
                  onChange(e);
                  setTimeout(() => trigger('referral.donation'), 0);
                }}
              />
            )}
          />
        </Stack>
        {watchReferral?.isEnableDonation ? (
          <Typography color={'white'} fontSize={'14px'} variant='h6' paddingTop={1}>
            If the buyer does not apply a referral code, a donation to ONETREEPLANTED will be made.
          </Typography>
        ) : null}
      </Box> */}
      {watchReferral?.isEnableDonation ? (
        <Box width={'100%'}>
          <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
            Percentage
          </Typography>
          <Controller
            name='referral.donation'
            control={control}
            render={({ field }) => (
              <AdminNumberFormat
                readOnly={(hasDeployed && defaultIsDonationEnable) || isDisabled}
                isAllowed={isAllowedPercentage}
                onValueChange={values => {
                  field.onChange(Number(values.floatValue));
                  trigger('referral.donation');
                }}
                isError={!!errors?.referral?.donation}
                errorMsg={errors?.referral?.donation?.message}
                placeholder={'0%'}
                {...field}
              />
            )}
          />
        </Box>
      ) : null}
      <Typography color={'white'} fontSize={'16px'} fontWeight={500} variant='h6'>
        Referral Level
      </Typography>
      <Box width={'100%'}>
        <Controller
          control={control}
          name='referral.referralLv'
          render={({ field }) => (
            <RadioGroup
              aria-disabled={hasDeployed}
              sx={{
                color: 'white',
                display: 'flex',
                flexDirection: 'row',
              }}
              {...field}
              onChange={handleReferralLevelChange}>
              <FormControlLabel
                disabled={(hasDeployed && defaultIsEnable) || isDisabled}
                value={ReferralLevel.LEVEL_1}
                sx={{
                  fontSize: '14px',
                  width: '50%',
                  fontWeight: '400',
                  '& .MuiRadio-root.Mui-disabled': {
                    color: 'white',
                  },
                }}
                control={
                  <Radio
                    sx={{
                      fontSize: '14px',
                      fontWeight: '400',
                      color: colors.neutral900,
                    }}
                  />
                }
                label={
                  <Typography fontSize={'14px'} fontWeight={400}>
                    First-Level Referrals
                  </Typography>
                }
              />
              <FormControlLabel
                disabled={(hasDeployed && defaultIsEnable) || isDisabled}
                sx={{
                  fontSize: '14px',
                  fontWeight: '400',
                  '& .MuiRadio-root.Mui-disabled': {
                    color: 'white',
                  },
                }}
                value={ReferralLevel.LEVEL_2}
                control={
                  <Radio
                    sx={{
                      color: colors.neutral900,
                    }}
                  />
                }
                label={
                  <Typography fontSize={'14px'} fontWeight={400}>
                    Two-Level Referrals
                  </Typography>
                }
              />
            </RadioGroup>
          )}
        />
      </Box>
      <Grid2 container columnSpacing={2}>
        <Grid2 size={6}>
          <Box width={'100%'}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Level 1 Percentage
            </Typography>
            <Controller
              name='referral.referLv1'
              control={control}
              render={({ field }) => (
                <AdminNumberFormat
                  isAllowed={isAllowedPercentage}
                  readOnly={(hasDeployed && defaultIsEnable) || isDisabled || isFirstLevelReferral}
                  onValueChange={values => {
                    field.onChange(Number(values.floatValue));
                    // Trigger validation for related percentage fields
                    triggerPercentageValidation();
                  }}
                  // Only show error if it's not the first level referral
                  isError={!isFirstLevelReferral && !!errors?.referral?.referLv1}
                  errorMsg={!isFirstLevelReferral ? errors?.referral?.referLv1?.message : undefined}
                  placeholder={'0%'}
                  {...field}
                />
              )}
            />
            {isFirstLevelReferral && (
              <Typography color={colors.neutral200} fontSize={'12px'} variant='body2' paddingTop={0.5}>
                Automatically set to match the main referral percentage
              </Typography>
            )}
          </Box>
        </Grid2>
        {watchReferral.isEnable && watchReferral.referralLv === ReferralLevel.LEVEL_2 ? (
          <Grid2 size={6}>
            <Box width={'100%'}>
              <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
                Level 2 Percentage
              </Typography>
              <Controller
                name='referral.referLv2'
                control={control}
                render={({ field }) => (
                  <AdminNumberFormat
                    isAllowed={isAllowedPercentage}
                    readOnly={(hasDeployed && defaultIsEnable) || isDisabled}
                    onValueChange={values => {
                      field.onChange(Number(values.floatValue));
                      // Trigger validation for related percentage fields
                      triggerPercentageValidation();
                    }}
                    isError={!!errors?.referral?.referLv2}
                    errorMsg={errors?.referral?.referLv2?.message}
                    placeholder={'0%'}
                    {...field}
                  />
                )}
              />
            </Box>
          </Grid2>
        ) : null}
      </Grid2>
    </Stack>
  );
}
